import React, { useEffect, useState } from 'react';
import { Box, useMediaQuery } from '@material-ui/core';
import { Trans, useTranslation } from 'react-i18next';

import {
    GridCol,
    Typography,
    Variant,
    Police,
    Alert,
    AlertVariant,
    ColorHex,
    Article,
    Spacer,
    Banner,
    DateText,
    BannerVariant
} from '../../../../styles';

import {
    ResumeMontant,
    ResumePret,
    ClavardageContexte,
    setContexteClavardage
} from '../../../../components';
import {
    ParcoursEmprunteur,
    emprunteur,
    EmprunteurStatut,
    IResumeOffre,
    ISession
} from '../../../../hooks';

interface IConfirmationHorsParcoursProps {
    isOntario: boolean;
    delaiApprobation: number;
    dateSignature: Date;
    typeParcours: ParcoursEmprunteur;
    emprunteurs: Array<emprunteur>;
    resumeOffre: IResumeOffre;
    session: ISession;
}

export const ConfirmationHorsParcours: React.FC<
    IConfirmationHorsParcoursProps
> = ({
    isOntario,
    delaiApprobation,
    dateSignature,
    typeParcours,
    emprunteurs,
    resumeOffre,
    session
}) => {
    const [t] = useTranslation(['Confirmation']);
    const isMobile = useMediaQuery('(max-width:640px)');
    const [tipBannerClosed, setTipBannerClosed] = useState(false);
    const [tipOntarioBannerClosed, setOntarioTipBannerClosed] = useState(false);

    // Sort sur une Shallow Copie pour avoir l'emprunteur connecte en premier
    const sortedEmprunteurs = [...emprunteurs].sort((x, y) =>
        x?.connecte === y?.connecte ? 0 : x?.connecte ? -1 : 1
    );

    const renderNextStepSection = () => {
        const shouldDisplayTwoSteps =
            typeParcours === ParcoursEmprunteur.COEMPRUNTEUR_GUY &&
            sortedEmprunteurs.length > 1 &&
            sortedEmprunteurs[1].statut !== EmprunteurStatut.CONFIRMEE;
        return (
            <>
                <Typography variant={Variant.p} police={Police.bold}>
                    {shouldDisplayTwoSteps
                        ? t('choixTitre2Steps')
                        : t('choixTitre1Step')}
                </Typography>
                <ul>
                    {/* On affiche l'etape 1 si coemprunteur ET le second coemprunteur n'as pas fini */}
                    {shouldDisplayTwoSteps && <li>{t('choixEtape1')}</li>}
                    <li>{t('choixEtape2')}</li>
                </ul>
                {!isMobile && <Spacer />}
            </>
        );
    };

    const renderGuySection = () => (
        <>
            {/* Si les deux coemprunteurs on finit -> on affiche pas le temps restant */}
            {typeParcours === ParcoursEmprunteur.COEMPRUNTEUR_GUY &&
                sortedEmprunteurs[1].statut !== EmprunteurStatut.CONFIRMEE && (
                    <>
                        <Typography variant={Variant.h3} police={Police.bold}>
                            {delaiApprobation > 0 ? (
                                <DateText
                                    translationKey="Confirmation:sousTitreDate"
                                    date={dateSignature}
                                >
                                    {{ date: dateSignature }}
                                </DateText>
                            ) : (
                                t('sousTitreToday')
                            )}
                        </Typography>
                        <Typography
                            variant={Variant.p}
                            article={Article.a3}
                            marginBottom="1.5rem"
                        >
                            {t('warningText')}
                        </Typography>
                        <Alert
                            isFullWidth
                            variant={AlertVariant.information}
                            dataTestId="attente_signature_martine"
                        >
                            {t('informationSignatureContrat')}
                        </Alert>
                    </>
                )}
        </>
    );

    const obtenirI18KeyAlerteCourrielChangementFrequence = (): string => {
        if (
            resumeOffre?.offreChoisie
                ?.dateProchainPaiementChangementFrequence !== null
        ) {
            return 'Confirmation:alertBasChangementFrequence';
        } else {
            return 'Confirmation:alertBasMemeFrequence';
        }
    };

    const renderAlertCourrielFinParcours = () => (
        <>
            {typeParcours === ParcoursEmprunteur.COEMPRUNTEUR_GUY &&
            sortedEmprunteurs[1].statut !== EmprunteurStatut.CONFIRMEE ? (
                <></>
            ) : (
                <Alert
                    variant={AlertVariant.information}
                    dataTestId="alerte_courriel_frequence"
                >
                    <Trans
                        i18nKey={obtenirI18KeyAlerteCourrielChangementFrequence()}
                    />
                </Alert>
            )}
        </>
    );

    useEffect(() => {
        setContexteClavardage(ClavardageContexte.CONFIRMATION);

        return () => {
            // Réinitialisation du contexte de clavardage
            setContexteClavardage(ClavardageContexte.DEFAULT);
        };
    }, []);

    return (
        <GridCol col="12" sm="8" md="6">
            <Box margin={isMobile ? '1rem 1rem 0 1rem' : '1.5rem 0 0 1.5rem'}>
                <Typography
                    variant={Variant.p}
                    police={Police.bold}
                    color={ColorHex.lightGraphite4}
                >
                    {t('titreRhn')}
                </Typography>
                <Typography
                    variant={Variant.h2}
                    dataTestId={'page_confirmation_titre'}
                >
                    {t('titre')}
                </Typography>
                <Alert
                    isFullWidth
                    variant={AlertVariant.confirmation}
                    dataTestId={'alerte_demande_enregistré'}
                >
                    {t('saved')}
                </Alert>
                {renderAlertCourrielFinParcours()}
                {renderGuySection()}
                {renderNextStepSection()}
                <ResumePret
                    isEditButtonDisplayed={false}
                    isTauxAssuranceDisplayed={false}
                    isSoldeSansAssuranceDisplayed={false}
                    session={session}
                    resumeOffre={resumeOffre}
                    isSimplifie={true}
                />
                <ResumeMontant
                    resumeOffre={resumeOffre}
                    isDetailAssurance={false}
                />
                {!tipBannerClosed && <Spacer all={5} sm={6} />}
                <Banner
                    variant={BannerVariant.TIP}
                    dataTestId="banner_conseil"
                    onClose={() => {
                        setTipBannerClosed(true);
                    }}
                >
                    <h2 slot="title">{t('tipTitle')}</h2>
                    <span slot="content">{t('tip')}</span>
                </Banner>
                {isOntario && (
                    <>
                        {!tipOntarioBannerClosed && <Spacer all={5} sm={6} />}
                        <Banner
                            variant={BannerVariant.SUGGESTION}
                            dataTestId="banner_regle_ontario"
                            onClose={() => {
                                setOntarioTipBannerClosed(true);
                            }}
                        >
                            <h2 slot="title">{t('alertOntarioTitle')}</h2>
                            <span slot="content">{t('alertOntario')}</span>
                        </Banner>
                    </>
                )}
                <Spacer all={5} sm={6} />
            </Box>
        </GridCol>
    );
};
